import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Separator } from "../ui/separator";
import { toast } from "sonner";
import { Frame, Layers, Shield, Save, RefreshCw, DollarSign, Loader2 } from "lucide-react";
import { API } from "../../apiConfig";


const FramePricingTab = ({ token }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [editedPrices, setEditedPrices] = useState({});

  useEffect(() => {
    fetchFramingData();
  }, []);

  const fetchFramingData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/frames/admin/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
      
      // Initialize edited prices
      const prices = {};
      response.data.frames.forEach(f => {
        prices[`frame_${f.frame_id}`] = { ...f.prices };
      });
      response.data.mats.forEach(m => {
        prices[`mat_${m.mat_id}`] = { ...m.prices };
      });
      response.data.glazing.forEach(g => {
        prices[`glazing_${g.glazing_id}`] = { ...g.prices };
      });
      setEditedPrices(prices);
    } catch (error) {
      console.error("Error fetching framing data:", error);
      toast.error("Failed to load framing options");
    } finally {
      setLoading(false);
    }
  };

  const handlePriceChange = (type, id, size, value) => {
    const key = `${type}_${id}`;
    setEditedPrices(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [size]: parseFloat(value) || 0
      }
    }));
  };

  const saveFramePrices = async (frameId) => {
    setSaving(`frame_${frameId}`);
    try {
      await axios.put(
        `${API}/frames/admin/frame/${frameId}/prices`,
        { prices: editedPrices[`frame_${frameId}`] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Frame prices updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update prices");
    } finally {
      setSaving(null);
    }
  };

  const saveMatPrices = async (matId) => {
    setSaving(`mat_${matId}`);
    try {
      await axios.put(
        `${API}/frames/admin/mat/${matId}/prices`,
        { prices: editedPrices[`mat_${matId}`] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Mat prices updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update prices");
    } finally {
      setSaving(null);
    }
  };

  const saveGlazingPrices = async (glazingId) => {
    setSaving(`glazing_${glazingId}`);
    try {
      await axios.put(
        `${API}/frames/admin/glazing/${glazingId}/prices`,
        { prices: editedPrices[`glazing_${glazingId}`] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success("Glazing prices updated");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update prices");
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const sizes = ["A4", "A3", "A2", "A1"];

  const PriceEditor = ({ type, item, onSave }) => {
    const key = `${type}_${item[`${type}_id`]}`;
    const prices = editedPrices[key] || {};
    const isSaving = saving === key;
    const itemId = item[`${type}_id`];

    return (
      <div className="p-4 border rounded-xl bg-card">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h4 className="font-medium">{item.name}</h4>
            <p className="text-xs text-muted-foreground">{item.description?.slice(0, 60)}...</p>
          </div>
          {item.is_active === false && (
            <Badge variant="secondary">Inactive</Badge>
          )}
        </div>
        
        <div className="grid grid-cols-4 gap-2 mb-3">
          {sizes.map(size => (
            <div key={size}>
              <Label className="text-xs text-muted-foreground">{size}</Label>
              <div className="relative">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">$</span>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  value={prices[size] || 0}
                  onChange={(e) => handlePriceChange(type, itemId, size, e.target.value)}
                  className="pl-6 h-9 text-sm"
                />
              </div>
            </div>
          ))}
        </div>

        <Button
          size="sm"
          onClick={() => onSave(itemId)}
          disabled={isSaving}
          className="w-full"
        >
          {isSaving ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Save className="w-4 h-4 mr-2" />
          )}
          Save Prices
        </Button>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            Frame Pricing Management
          </h2>
          <p className="text-sm text-muted-foreground">
            Update prices for frames, mats, and glazing options
          </p>
        </div>
        <Button variant="outline" onClick={fetchFramingData}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="frames" className="space-y-4">
        <TabsList>
          <TabsTrigger value="frames" className="gap-2">
            <Frame className="w-4 h-4" />
            Frames ({data?.frames?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="mats" className="gap-2">
            <Layers className="w-4 h-4" />
            Mats ({data?.mats?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="glazing" className="gap-2">
            <Shield className="w-4 h-4" />
            Glazing ({data?.glazing?.length || 0})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="frames" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Frame className="w-4 h-4" />
                Frame Styles
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.frames?.filter(f => !f.is_custom_quote).map(frame => (
                  <PriceEditor
                    key={frame.frame_id}
                    type="frame"
                    item={frame}
                    onSave={saveFramePrices}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="mats" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Layers className="w-4 h-4" />
                Mat Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.mats?.map(mat => (
                  <PriceEditor
                    key={mat.mat_id}
                    type="mat"
                    item={mat}
                    onSave={saveMatPrices}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="glazing" className="space-y-4">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Glazing Options
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {data?.glazing?.map(glazing => (
                  <PriceEditor
                    key={glazing.glazing_id}
                    type="glazing"
                    item={glazing}
                    onSave={saveGlazingPrices}
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Info Card */}
      <Card className="bg-amber-50 border-amber-200">
        <CardContent className="pt-4">
          <div className="flex items-start gap-3">
            <DollarSign className="w-5 h-5 text-amber-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-medium text-amber-800">Pricing Note</p>
              <p className="text-amber-700 mt-1">
                Prices are stored in memory and will reset when the server restarts. 
                For persistent pricing, consider migrating to database storage.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default FramePricingTab;
